import { useMemo } from 'react';
import tw, { TwStyle } from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import { GarmResultsType } from '@insights_module/types/BrandSafety';
import { GARM_RISK_LEVEL } from '@insights_module/constants/garm';

const Container = tw.div`flex items-end justify-start flex-wrap gap-2 my-2`;
const StyledGarmLabel = tw.div`px-[12px] py-[4px] rounded-[4px] text-text-and-icon-invert text-[16px] font-semibold leading-normal max-w-[368px] truncate`;

const GARM_LABEL_STYLE_MAPPER: { [key: string]: TwStyle } = {
  [GARM_RISK_LEVEL.unrelated]: tw`bg-garm-unrelated-bg text-text-and-icon-invert` as TwStyle,
  [GARM_RISK_LEVEL.low]: tw`bg-garm-low-bg text-text-and-icon-invert` as TwStyle,
  [GARM_RISK_LEVEL.medium]: tw`bg-garm-medium-bg text-text-and-icon-invert` as TwStyle,
  [GARM_RISK_LEVEL.high]: tw`bg-garm-high-bg text-text-and-icon-invert` as TwStyle,
  [GARM_RISK_LEVEL.floor]: tw`bg-floor-bg text-text-and-icon-invert` as TwStyle,
};

export const TranscriptGarmLabels = ({
  garmTracebility,
}: {
  garmTracebility: GarmResultsType | undefined;
}) => {
  const { t } = useTranslation();
  const { selectedGarmCategory } = useEpisodeDetailsContext();

  const labels = useMemo(() => {
    if (!garmTracebility) return [];

    const relatedItems = Object.entries(garmTracebility)
      .map(([category, riskLevel]) => ({
        category,
        riskLevel: riskLevel.toLocaleLowerCase(),
      }))
      .filter((item) => item.riskLevel !== GARM_RISK_LEVEL.unrelated);

    if (selectedGarmCategory) {
      const categoryItem = relatedItems.find((item) => item.category === selectedGarmCategory);
      const categoryItemIndex = relatedItems.findIndex(
        (item) => item.category === selectedGarmCategory
      );

      if (categoryItem) {
        relatedItems.splice(categoryItemIndex, 1);
        relatedItems.unshift(categoryItem);
      }
    }

    return relatedItems;
  }, [garmTracebility, selectedGarmCategory]);

  if (!garmTracebility || !labels.length) return null;

  return (
    <Container>
      {labels.map((label) => {
        if (label.riskLevel.toLocaleLowerCase() === GARM_RISK_LEVEL.unrelated) return null;

        return (
          <StyledGarmLabel
            css={[
              GARM_LABEL_STYLE_MAPPER[label.riskLevel.toLocaleLowerCase()],
              selectedGarmCategory && selectedGarmCategory !== label.category && tw`opacity-50`,
            ]}
            key={label.category}
            title={
              selectedGarmCategory === label.category
                ? `Deselect ${t(`insights.garm_category.${label.category}`)}`
                : `Select ${t(`insights.garm_category.${label.category}`)}`
            }>
            {t(`insights.garm_category.${label.category}`)}
          </StyledGarmLabel>
        );
      })}
    </Container>
  );
};
